import { Component } from '@theme/component';

/**
 * Poll section (`sections/poll.liquid` + `snippets/poll-option.liquid`).
 *
 * Votes are tallied by a Google Apps Script web app writing to a Google Sheet
 * (see apps-script/README.md). The endpoint is unauthenticated and Apps Script
 * always answers HTTP 200 — success is the `ok` field in the JSON body, never
 * `response.ok`.
 *
 * The localStorage dedupe (`poll:<poll_id>` → chosen option id) is a COURTESY,
 * not enforcement — anyone can clear storage or script the endpoint directly.
 * Never build anything on top of it that assumes one vote per person.
 *
 * Server-rendered options are the source of truth: this component only updates
 * counts and toggles state, it never builds the option list. Percentages are
 * computed from the options actually rendered (a deleted option's stale sheet
 * rows are excluded), so bars always sum coherently.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [status]
 * @property {HTMLElement} [error]
 * @property {HTMLButtonElement[]} [options]
 *
 * @extends {Component<Refs>}
 */
class PollComponent extends Component {
  /** @type {AbortController | undefined} */
  #abort;

  /** @type {Record<string, number>} Last known tallies, keyed by option (block) id. */
  #tallies = {};

  /** @type {boolean} Whether tallies hold real data (fetched or optimistic). */
  #loaded = false;

  /** @type {string | null} The option id this visitor voted for. */
  #votedId = null;

  /** @type {boolean} A vote request is in flight. */
  #voting = false;

  connectedCallback() {
    super.connectedCallback();

    this.#abort = new AbortController();
    this.#votedId = this.#storedVote();

    if (!this.#endpoint) {
      console.warn('[poll] No Apps Script endpoint configured — voting is disabled.', this);
    }

    this.#render();

    // Only fetch on load when there is something to show (§ saves a request
    // per pageview for the common not-yet-voted, results-hidden case).
    if (this.#endpoint && (this.#votedId || this.#showResultsBeforeVote)) {
      this.#loadTallies();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
  }

  // A section morph (theme editor setting change, Section Rendering API)
  // resets the markup to its stateless server-rendered form — reapply.
  updatedCallback() {
    this.#render();
  }

  get #endpoint() {
    return this.getAttribute('data-endpoint') ?? '';
  }

  get #pollId() {
    return this.getAttribute('data-poll-id') ?? '';
  }

  get #allowRevote() {
    // In the editor the merchant can always test repeatedly.
    return this.hasAttribute('data-allow-revote') || this.#designMode;
  }

  get #showResultsBeforeVote() {
    return this.hasAttribute('data-show-results');
  }

  get #designMode() {
    return Boolean(window.Shopify?.designMode);
  }

  get #storageKey() {
    return `poll:${this.#pollId}`;
  }

  /** @returns {HTMLButtonElement[]} Connected option buttons (refs can briefly hold detached nodes after a block removal). */
  get #options() {
    return (this.refs.options ?? []).filter((option) => option.isConnected);
  }

  /** @returns {string | null} */
  #storedVote() {
    if (this.#designMode) return null; // skip dedupe so the merchant can test
    try {
      return localStorage.getItem(this.#storageKey);
    } catch {
      return null;
    }
  }

  /** @param {string} optionId */
  #storeVote(optionId) {
    if (this.#designMode) return; // never write storage in the editor
    try {
      localStorage.setItem(this.#storageKey, optionId);
    } catch {
      // Storage unavailable (private mode) — dedupe is best-effort anyway.
    }
  }

  /**
   * Records a vote. Wired declaratively via `on:click="/vote"` on each option.
   * Optimistic: the local tally increments and results render immediately;
   * the server's authoritative tallies reconcile when the (slow, ~1–2s cold
   * Apps Script) request lands. On failure the previous state is restored and
   * the buttons re-enable — tapping an option again retries.
   *
   * Note on revotes: the backend only increments (there is no decrement), so
   * a revote casts an additional vote for the new option; the previous vote
   * stays counted. Accepted for this low-stakes widget.
   *
   * @param {Event} event
   */
  vote = async (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('.poll__option');
    if (!(button instanceof HTMLButtonElement)) return;

    const optionId = button.dataset.optionId;
    if (!optionId || this.#voting || !this.#endpoint) return;
    if (this.#votedId && !this.#allowRevote) return;

    const previous = {
      tallies: { ...this.#tallies },
      loaded: this.#loaded,
      votedId: this.#votedId,
    };

    this.#voting = true;
    this.#hideError();
    button.setAttribute('aria-busy', 'true');

    // Optimistic render.
    this.#tallies[optionId] = (this.#tallies[optionId] ?? 0) + 1;
    this.#loaded = true;
    this.#votedId = optionId;
    this.#render();

    try {
      const response = await fetch(this.#endpoint, {
        method: 'POST',
        // text/plain keeps this a CORS-simple request: Apps Script cannot
        // answer an OPTIONS preflight, which application/json would trigger.
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify({
          poll_id: this.#pollId,
          option_id: optionId,
          label: button.dataset.label ?? '',
        }),
        // Apps Script 302s from script.google.com to script.googleusercontent.com.
        redirect: 'follow',
        signal: this.#abort?.signal,
      });
      const data = await response.json();
      if (!data?.ok) throw new Error(typeof data?.error === 'string' ? data.error : 'vote_rejected');

      if (data.tallies && typeof data.tallies === 'object') {
        this.#tallies = normalizeTallies(data.tallies);
      }
      this.#storeVote(optionId);

      window.publishStorefrontEvent?.('poll_voted', {
        poll_id: this.#pollId,
        section_id: this.getAttribute('data-section-id') ?? '',
        block_id: optionId,
        label: button.dataset.label ?? '',
        total: this.#renderedTotal(),
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;

      // Restore the pre-vote state — never leave the UI stuck disabled.
      this.#tallies = previous.tallies;
      this.#loaded = previous.loaded;
      this.#votedId = previous.votedId;
      this.#showError();

      window.publishStorefrontEvent?.('poll_vote_failed', {
        poll_id: this.#pollId,
        section_id: this.getAttribute('data-section-id') ?? '',
        block_id: optionId,
        error_type: error instanceof TypeError ? 'network' : 'server_rejected',
      });
    } finally {
      this.#voting = false;
      button.removeAttribute('aria-busy');
      this.#render();
      this.#recoverFocus();
    }
  };

  /** Fetches authoritative tallies (page load for voted / results-first states). */
  async #loadTallies() {
    try {
      const url = `${this.#endpoint}?poll_id=${encodeURIComponent(this.#pollId)}`;
      const response = await fetch(url, { redirect: 'follow', signal: this.#abort?.signal });
      const data = await response.json();
      if (!data?.ok || !data.tallies || typeof data.tallies !== 'object') return;

      this.#tallies = normalizeTallies(data.tallies);
      this.#loaded = true;
      this.#render();
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      // Results just stay blank; voting still works.
      console.warn('[poll] Could not load tallies.', error);
    }
  }

  /** @returns {number} Total votes across the RENDERED options only. */
  #renderedTotal() {
    return this.#options.reduce((sum, option) => sum + (this.#tallies[option.dataset.optionId ?? ''] ?? 0), 0);
  }

  /**
   * Applies the full component state to the DOM. Idempotent — safe to re-run
   * after morphs, votes, and tally loads alike.
   */
  #render() {
    const options = this.#options;
    if (options.length === 0) return;

    const showResults = Boolean(this.#votedId) || this.#showResultsBeforeVote;
    const disableAll = this.#voting || Boolean(this.#votedId && !this.#allowRevote) || !this.#endpoint;
    const total = this.#renderedTotal();

    this.setAttribute('data-state', showResults ? 'results' : 'idle');

    for (const option of options) {
      const optionId = option.dataset.optionId ?? '';
      const count = this.#tallies[optionId] ?? 0;
      const percent = total > 0 ? Math.round((count / total) * 100) : 0;

      option.disabled = disableAll;
      option.setAttribute('aria-pressed', this.#votedId === optionId ? 'true' : 'false');
      // Unitless on purpose — the stylesheet multiplies it into the width of
      // the track after the image tile (see .poll__option-bar).
      option.style.setProperty('--poll-option-pct', showResults && this.#loaded ? String(percent) : '0');

      const percentElement = option.querySelector('[data-percent]');
      if (percentElement) {
        percentElement.textContent = showResults && this.#loaded ? `${percent}%` : '';
      }
    }

    const { status } = this.refs;
    if (status) {
      const text = showResults && this.#loaded ? this.#votesText(total) : '';
      if (status.textContent !== text) status.textContent = text;
    }
  }

  /** @param {number} total */
  #votesText(total) {
    const template =
      (total === 1
        ? this.getAttribute('data-text-votes-one')
        : this.getAttribute('data-text-votes-other')) ?? '[count]';
    return template.replace('[count]', String(total));
  }

  #showError() {
    const { error } = this.refs;
    if (error) error.hidden = false;
  }

  #hideError() {
    const { error } = this.refs;
    if (error) error.hidden = true;
  }

  /**
   * Disabling the focused option button drops focus to `<body>`; move it to
   * the status line (tabindex="-1") so keyboard/screen-reader context stays
   * inside the poll. When the buttons re-enabled after an error, put focus
   * back on them instead so retrying is one keypress away.
   */
  #recoverFocus() {
    const active = document.activeElement;
    if (active && active !== document.body && active !== document.documentElement) return;

    const enabled = this.#options.find((option) => !option.disabled);
    const fallback = enabled ?? this.refs.status;
    fallback?.focus?.({ preventScroll: true });
  }
}

/**
 * Coerces a server tallies map to safe non-negative integers.
 *
 * @param {Record<string, unknown>} raw
 * @returns {Record<string, number>}
 */
function normalizeTallies(raw) {
  /** @type {Record<string, number>} */
  const tallies = {};
  for (const [key, value] of Object.entries(raw)) {
    const count = Number(value);
    if (Number.isFinite(count) && count >= 0) tallies[key] = Math.floor(count);
  }
  return tallies;
}

if (!customElements.get('poll-component')) {
  customElements.define('poll-component', PollComponent);
}
